/**
 * Two-Phase Lap Processing
 *
 * Optimized for React hooks with expensive caching in Phase 1 and
 * cheap selection-dependent calculations in Phase 2.
 *
 * Phase 1 (computeBaseDataPoints): Run once per lap, cache result
 * Phase 2 (applyTimeDriftOverlay): Run on selection change, very fast
 */

import { Lap } from '../../domain/lap/Lap'
import { TimeDriftResult } from '../../domain/lap/TimeDriftCalculator'
import {
  BaseLapDataPoint,
  PreprocessedDataPoint,
  RecalibrationRefData,
} from './types'
import { interpolateTimeDriftWithRate } from './timeDriftInterpolation'

/**
 * Compute base data points for a lap (EXPENSIVE - without time drift).
 * This only needs to run once per lap, not on every selection change.
 *
 * Phase 1 of two-phase processing: Extract all telemetry data without
 * selection-dependent calculations (time drift, distance recalibration).
 *
 * @param lap Domain Lap object
 * @param coastingMap Pre-computed coasting intensity map (distance → intensity)
 * @param getCoastingFn Optional function to look up coasting at a distance (for interpolation)
 */
export function computeBaseDataPoints(
  lap: Lap,
  coastingMap: Map<number, number>,
  getCoastingFn?: (
    coastingMap: Map<number, number>,
    distance: number
  ) => number | null
): BaseLapDataPoint[] {
  const gpsPoints = lap.processedTelemetryData.gpsPoints
  const gForce = lap.processedTelemetryData.gForce
  const acceleration = lap.processedTelemetryData.acceleration
  const leanAngle = lap.processedTelemetryData.leanAngle
  const lapStartTime = lap.startTime.getTime()

  return gpsPoints.map((point, index) => {
    const gForcePoint = gForce[index]
    const accelPoint = acceleration[index]
    const leanAnglePoint = leanAngle[index]

    const lapRelativeDistance = point.distanceMeters
    const lapRelativeTimeMs = point.timestamp.getTime() - lapStartTime

    // Look up coasting - use provided function for interpolation or simple map lookup
    let coasting: number | undefined
    if (coastingMap.size > 0) {
      if (getCoastingFn) {
        const value = getCoastingFn(coastingMap, lapRelativeDistance)
        coasting = value !== null ? value : undefined
      } else {
        // Simple rounded distance lookup
        coasting = coastingMap.get(Math.round(lapRelativeDistance))
      }
    }

    return {
      originalDistance: point.distanceMeters,
      lapRelativeDistance,
      time: lapRelativeTimeMs / 1000,
      speed: point.speedKmh,
      coasting,
      lateralG: gForcePoint?.lateralG,
      leanAngle: leanAnglePoint?.leanAngleDegrees,
      acceleration:
        accelPoint?.longitudinalMs2 !== undefined
          ? accelPoint.longitudinalMs2 / 9.81
          : undefined,
      accelerationSource: accelPoint?.source,
      accelerationSuspect: accelPoint?.speedTrendMismatch,
      lat: point.latitude,
      lng: point.longitude,
    }
  })
}

/**
 * Apply time drift overlay to base data points (CHEAP - just adds time drift values).
 * This is fast because we're not recomputing speed, coasting, G-force, etc.
 *
 * Phase 2 of two-phase processing: Apply selection-dependent calculations
 * (time drift, distance recalibration) to cached base data points.
 *
 * OPTIMIZATION: If no time drift or recalibration needed, can use cached result.
 * OPTIMIZATION: Uses ultra-fast batch recalibration with raw arrays.
 *
 * @param baseDataPoints Base data points from computeBaseDataPoints
 * @param timeDriftResult Time drift calculation result (optional)
 * @param refData Reference data for distance recalibration (optional)
 * @param batchRecalibrateFn Function to perform batch recalibration
 */
export function applyTimeDriftOverlay(
  baseDataPoints: BaseLapDataPoint[],
  timeDriftResult: TimeDriftResult | undefined,
  refData: RecalibrationRefData | undefined,
  batchRecalibrateFn?: (
    targetLats: number[],
    targetLons: number[],
    refLats: number[],
    refLons: number[],
    refDistances: number[]
  ) => number[]
): PreprocessedDataPoint[] {
  const hasTimeDrift = timeDriftResult && timeDriftResult.points.length > 0
  const hasRecalibration = refData && refData.refLats.length > 0

  // FAST PATH: No modifications needed
  if (!hasTimeDrift && !hasRecalibration) {
    return baseDataPoints.map((basePoint) => ({
      distance: basePoint.originalDistance,
      lapRelativeDistance: basePoint.lapRelativeDistance,
      time: basePoint.time,
      speed: basePoint.speed,
      timeDrift: undefined,
      coasting: basePoint.coasting,
      lateralG: basePoint.lateralG,
      leanAngle: basePoint.leanAngle,
      acceleration: basePoint.acceleration,
      accelerationSource: basePoint.accelerationSource,
      accelerationSuspect: basePoint.accelerationSuspect,
      lat: basePoint.lat,
      lng: basePoint.lng,
    }))
  }

  // ULTRA-OPTIMIZED PATH: Use batch recalibration with raw arrays
  let recalibratedDistances: number[] | undefined
  if (hasRecalibration && batchRecalibrateFn) {
    const len = baseDataPoints.length
    const targetLats = new Array<number>(len)
    const targetLons = new Array<number>(len)
    for (let i = 0; i < len; i++) {
      targetLats[i] = baseDataPoints[i].lat
      targetLons[i] = baseDataPoints[i].lng
    }
    recalibratedDistances = batchRecalibrateFn(
      targetLats,
      targetLons,
      refData!.refLats,
      refData!.refLons,
      refData!.refDistances
    )
  }

  // Apply time drift interpolation
  return baseDataPoints.map((basePoint, index) => {
    const chartDistance = recalibratedDistances
      ? recalibratedDistances[index]
      : basePoint.originalDistance

    let timeDrift: number | undefined
    let timeDriftRate: number | undefined
    if (hasTimeDrift) {
      // Interpolate using the recalibrated distance: drift points are keyed by
      // the REFERENCE lap's distances, so the lookup must be in that domain.
      // The lap's own distance channel (lapRelativeDistance) may disagree with
      // it when laps come from different devices.
      const interpolated = interpolateTimeDriftWithRate(
        timeDriftResult!,
        chartDistance
      )
      timeDrift = interpolated.timeDrift
      timeDriftRate = interpolated.timeDriftRate
    }

    return {
      distance: chartDistance,
      lapRelativeDistance: basePoint.lapRelativeDistance,
      time: basePoint.time,
      speed: basePoint.speed,
      timeDrift,
      timeDriftRate,
      coasting: basePoint.coasting,
      lateralG: basePoint.lateralG,
      leanAngle: basePoint.leanAngle,
      acceleration: basePoint.acceleration,
      accelerationSource: basePoint.accelerationSource,
      accelerationSuspect: basePoint.accelerationSuspect,
      lat: basePoint.lat,
      lng: basePoint.lng,
    }
  })
}
